import { Download, FileText, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import EmptyState from "../../components/EmptyState.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import StatCard from "../../components/StatCard.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { getMasterData, getReportLogs, getSettings } from "../../firebase/firestore.js";
import { getMonthRange } from "../../utils/date.js";
import { exportReportPdf, exportRowsToExcel } from "../../utils/export.js";
import { buildSavedLogReportRows, summarizeReportRows } from "./adminUtils.js";

const now = new Date();
const defaultRange = getMonthRange(now.getFullYear(), now.getMonth() + 1);

const reportColumns = [
  { key: "date", header: "Date" },
  { key: "day", header: "Day" },
  { key: "roomNumber", header: "Room" },
  { key: "className", header: "Class" },
  { key: "sectionName", header: "Section" },
  { key: "subjectName", header: "Subject" },
  { key: "teacherName", header: "Teacher" },
  { key: "schedule", header: "Schedule" },
  { key: "actualIn", header: "Actual IN" },
  { key: "actualOut", header: "Actual OUT" },
  { key: "entryStatusLabel", header: "Entry Status" },
  { key: "exitStatusLabel", header: "Exit Status" },
  { key: "classStatus", header: "Class Status" },
  { key: "suspensionReason", header: "Reason" }
];

export default function ReportsPage() {
  const [master, setMaster] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasRun, setHasRun] = useState(false);
  const [filters, setFilters] = useState({
    month: String(now.getMonth() + 1).padStart(2, "0"),
    year: String(now.getFullYear()),
    from: defaultRange.from,
    to: defaultRange.to,
    teacherId: "",
    classId: "",
    subjectId: "",
    roomId: ""
  });
  const { showToast } = useToast();

  async function loadBase() {
    setLoading(true);
    try {
      const [nextMaster, nextSettings] = await Promise.all([getMasterData(), getSettings()]);
      setMaster(nextMaster);
      setSettings(nextSettings);
      setLoading(false);
    } catch (error) {
      showToast(error.message || "Unable to load reports", "error");
      setLoading(false);
    }
  }

  async function runReport() {
    setLoading(true);
    try {
      const reportLogs = await getReportLogs(filters);
      setLogs(reportLogs);
      setHasRun(true);
    } catch (error) {
      showToast(error.message || "Unable to run report", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBase();
  }, []);

  function applyMonth(month = filters.month, year = filters.year) {
    const range = getMonthRange(year, month);
    setFilters((value) => ({ ...value, month, year, from: range.from, to: range.to }));
  }

  const reportRows = useMemo(() => {
    return buildSavedLogReportRows(logs);
  }, [logs]);

  const summary = useMemo(() => summarizeReportRows(reportRows), [reportRows]);
  const selectedTeacher = master?.teachers?.find((teacher) => teacher.id === filters.teacherId);
  const reportTitle = selectedTeacher ? `Teacher Monthly Report - ${selectedTeacher.name}` : "Attendance / Time Tracking Report";

  function exportExcel() {
    exportRowsToExcel(
      reportRows.map((row) => ({
        Date: row.date,
        Day: row.day,
        Room: row.roomNumber,
        Class: row.className,
        Section: row.sectionName,
        Subject: row.subjectName,
        Teacher: row.teacherName,
        Schedule: row.schedule,
        "Actual IN": row.actualIn,
        "Actual OUT": row.actualOut,
        "Entry Status": row.entryStatusLabel,
        "Exit Status": row.exitStatusLabel,
        "Class Status": row.classStatus,
        "Late Entry Minutes": row.lateEntryMinutes,
        "Early Entry Minutes": row.earlyEntryMinutes,
        "Late Exit Minutes": row.lateExitMinutes,
        "Early Exit Minutes": row.earlyExitMinutes,
        "Suspension Reason": row.suspensionReason,
        "Updated By": row.updatedByName
      })),
      "pariksit-report.xlsx",
      "Report"
    );
  }

  function exportPdf() {
    exportReportPdf({
      instituteName: settings?.instituteName || "PARIKSIT",
      title: reportTitle,
      subtitle: `${filters.from} to ${filters.to}`,
      summary,
      columns: reportColumns,
      rows: reportRows,
      filename: "pariksit-report.pdf"
    });
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Admin</p>
          <h2>Reports</h2>
        </div>
        <div className="toolbar-actions">
          <button className="btn btn-ghost" type="button" onClick={exportExcel} disabled={!reportRows.length}><Download size={17} />Excel</button>
          <button className="btn btn-ghost" type="button" onClick={exportPdf} disabled={!reportRows.length}><FileText size={17} />PDF</button>
          <button className="btn btn-primary" type="button" onClick={() => runReport()}><RefreshCw size={17} />Run</button>
        </div>
      </div>

      <div className="filters-row">
        <select value={filters.month} onChange={(event) => applyMonth(event.target.value, filters.year)}>
          {Array.from({ length: 12 }).map((_, index) => (
            <option key={index + 1} value={String(index + 1).padStart(2, "0")}>
              {new Date(2024, index, 1).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>
        <input value={filters.year} onChange={(event) => applyMonth(filters.month, event.target.value)} inputMode="numeric" />
        <input type="date" value={filters.from} onChange={(event) => setFilters((value) => ({ ...value, from: event.target.value }))} />
        <input type="date" value={filters.to} onChange={(event) => setFilters((value) => ({ ...value, to: event.target.value }))} />
        <select value={filters.teacherId} onChange={(event) => setFilters((value) => ({ ...value, teacherId: event.target.value }))}>
          <option value="">All teachers</option>
          {(master?.teachers || []).map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.name}</option>)}
        </select>
        <select value={filters.classId} onChange={(event) => setFilters((value) => ({ ...value, classId: event.target.value }))}>
          <option value="">All classes</option>
          {(master?.classes || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.subjectId} onChange={(event) => setFilters((value) => ({ ...value, subjectId: event.target.value }))}>
          <option value="">All subjects</option>
          {(master?.subjects || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.roomId} onChange={(event) => setFilters((value) => ({ ...value, roomId: event.target.value }))}>
          <option value="">All rooms</option>
          {(master?.rooms || []).map((item) => <option key={item.id} value={item.id}>{item.roomNumber}</option>)}
        </select>
      </div>

      {hasRun && selectedTeacher ? (
        <article className="card report-banner">
          <h3>{selectedTeacher.name}</h3>
          <p>Complete monthly teacher summary for {filters.from} to {filters.to}</p>
        </article>
      ) : null}

      {loading ? (
        <SkeletonList count={3} />
      ) : hasRun && reportRows.length ? (
        <>
          <div className="stats-grid">
            {Object.entries(summary).map(([label, value], index) => (
              <StatCard key={label} label={label} value={value} icon={index % 2 === 0 ? "📊" : "⏱️"} tone={index % 4 === 0 ? "blue" : index % 4 === 1 ? "green" : index % 4 === 2 ? "orange" : "purple"} />
            ))}
          </div>
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  {reportColumns.map((column) => <th key={column.key}>{column.header}</th>)}
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row) => (
                  <tr key={`${row.date}_${row.routineId}_${row.actualIn}_${row.actualOut}`}>
                    {reportColumns.map((column) => (
                      <td key={column.key}>
                        {column.key.includes("Status") ? <StatusChip status={row[column.key]} /> : row[column.key] || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <EmptyState
          icon="🔎"
          title={hasRun ? "No saved time logs" : "Choose filters, then run report"}
          text={hasRun ? "Only saved IN/OUT or suspended records appear here. Routine-only schedules are not shown." : "Report data is not loaded by default to reduce Firebase reads."}
        />
      )}
    </section>
  );
}
